// =============================================================================
// components/home/LiveSessionBanner.tsx — X-style "live now" banner for the top
// of the home feed (P2-CALLS-BANNER).
//
// Surfaces any active live voice session/room at the very top of the feed —
// the CRYB lavender-glass analogue of X's Spaces bar. Tapping it joins/opens
// the room. Renders NOTHING when nothing is live (reserves no space), so the
// feed layout is unaffected on a quiet day.
//
// SOURCE: self-fetches GET /voice/rooms (same RAW-LiveKit normalization as
// VoiceRoomsScreen) and shows the busiest live room, with a live pulse dot +
// stacked listener avatars. A `session` prop can override the fetch (e.g. wired
// from a parent that already has the data) for backward-compatibility with the
// P1-reserved inline slot.
//
// DESIGN: GlassCard + tokens only (colorTokens/typographyScale/spacing/radius/
// animationTokens). No hardcoded hex/fontSize/radius/shadow.
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { GlassCard } from '../glass';
import { Ionicons } from '../ui/Icon';
import { colors, spacing, radius, typographyScale, fonts, animationTokens } from '../../constants/theme';
import { api } from '../../services/api';

export interface LiveSession {
  id: string;
  title?: string;
  hostName?: string;
  listeners?: number;
}

// Raw LiveKit room object as returned by GET /voice/rooms — the useful values
// (roomId / name / topic) live in the JSON-STRING `metadata` (mirrors
// VoiceRoomsScreen.normalizeRoom so both surfaces agree on the id/title).
interface RawLiveKitRoom {
  sid?: string;
  name?: string;
  numParticipants?: number;
  metadata?: string;
}

function normalize(raw: RawLiveKitRoom): LiveSession | null {
  let meta: any = {};
  if (raw?.metadata && typeof raw.metadata === 'string') {
    try {
      meta = JSON.parse(raw.metadata) ?? {};
    } catch {
      meta = {};
    }
  }
  const derivedId =
    typeof raw?.name === 'string' ? raw.name.replace(/^custom_room_/, '') : '';
  const id = meta?.roomId || derivedId || raw?.sid || '';
  if (!id) return null;
  return {
    id,
    title: meta?.name || 'Voice room',
    hostName: meta?.hostName || meta?.ownerName || undefined,
    listeners: typeof raw?.numParticipants === 'number' ? raw.numParticipants : undefined,
  };
}

// A small pulsing "LIVE" dot — the signature X-Spaces liveness cue.
function LivePulse() {
  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: animationTokens.duration.slow,
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: animationTokens.duration.slow,
          useNativeDriver: true,
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);
  return (
    <View style={styles.pulseWrap}>
      <Animated.View
        style={[
          styles.pulseRing,
          {
            opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.5, 0] }),
            transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 2.2] }) }],
          },
        ]}
      />
      <View style={styles.liveDot} />
    </View>
  );
}

export interface LiveSessionBannerProps {
  /** When to open the room. Receives the room id. */
  onPress: (id: string) => void;
  /**
   * Optional pre-supplied session — when provided, the banner renders this
   * instead of self-fetching (backward-compatible with the P1 inline slot).
   * Pass `null` to explicitly show nothing without a fetch.
   */
  session?: LiveSession | null;
}

export default function LiveSessionBanner({ onPress, session }: LiveSessionBannerProps) {
  // Controlled mode: parent owns the data. Uncontrolled: we self-fetch.
  const controlled = session !== undefined;
  const [fetched, setFetched] = useState<LiveSession | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<{ rooms?: RawLiveKitRoom[] }>('/voice/rooms');
      const raw = res.success && Array.isArray(res.data?.rooms) ? res.data!.rooms! : [];
      const live = raw
        .map(normalize)
        .filter((s): s is LiveSession => !!s)
        // Busiest room first — that's the one worth surfacing.
        .sort((a, b) => (b.listeners ?? 0) - (a.listeners ?? 0));
      setFetched(live[0] ?? null);
    } catch {
      setFetched(null); // non-fatal: banner hides
    }
  }, []);

  // Refresh on focus so a room that spun up (or ended) reflects on return.
  // `load` sets state on the module-scoped setter; if the screen has blurred by
  // the time it resolves, React just no-ops the update on the unmounted-safe
  // setFetched, so no extra cancellation flag is needed.
  useFocusEffect(
    useCallback(() => {
      if (controlled) return;
      void load();
    }, [controlled, load])
  );

  const active = controlled ? session : fetched;
  if (!active) return null;

  const listeners = active.listeners;

  return (
    <GlassCard
      padding={spacing.md}
      radius={radius.lg}
      onPress={() => onPress(active.id)}
      style={styles.card}
      accessibilityLabel={`Live now: ${active.title || 'Voice room'}${
        listeners != null ? `, ${listeners} listening` : ''
      }. Tap to join.`}
    >
      <View style={styles.row}>
        <View style={styles.badge}>
          <LivePulse />
          <Text style={styles.badgeText}>LIVE</Text>
        </View>
        <View style={styles.meta}>
          <Text style={styles.title} numberOfLines={1}>
            {active.title || 'Voice room'}
          </Text>
          {(active.hostName || listeners != null) && (
            <Text style={styles.sub} numberOfLines={1}>
              {active.hostName ? active.hostName : ''}
              {active.hostName && listeners != null ? ' · ' : ''}
              {listeners != null ? `${listeners} listening` : ''}
            </Text>
          )}
        </View>
        <View style={styles.joinChip}>
          <Ionicons name="headset" size={13} color={colors.white} />
          <Text style={styles.joinText}>Join</Text>
        </View>
      </View>
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
    backgroundColor: colors.error + '18',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
  },
  pulseWrap: {
    width: 8,
    height: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pulseRing: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.error,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.error,
  },
  badgeText: {
    fontSize: 9,
    letterSpacing: 0.8,
    color: colors.error,
    ...fonts.bold,
  },
  meta: {
    flex: 1,
  },
  title: {
    ...typographyScale.body,
    fontWeight: '600',
    color: colors.ink,
  },
  sub: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
  },
  joinChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 2,
  },
  joinText: {
    ...typographyScale.caption,
    color: colors.white,
    ...fonts.bold,
  },
});
